import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeliverableDto, DeliverableKind } from "../../shared/dto";
import { AutomationTimingEstimate } from "../AutomationTimingEstimate";
import { WalkFeedback, type WalkPoint } from "../WalkFeedback";
import { DocOverlay, readerProgressState } from "../doc/DocOverlay";
import { ReaderApprovalAction, WalkCaptureLayer } from "../doc/walk";
import { DeliverableNotes, FeedbackComposer } from "./Notes";
import { ReviewLoopIntro, humanDocTitle, isRecord } from "./shared";

export function DeliverableCard(props: {
  kind: DeliverableKind;
  deliverable?: DeliverableDto;
  canComment: boolean;
  busyAction: string | null;
  csrfToken?: string;
  /** F104: the partner's written reason when they returned the page. */
  staffReturnedReason?: string | null;
  t: (key: string) => string;
  onFeedback?: (payload: { text?: string | null; audioAttachmentId?: string | null; pointerTrace?: WalkPoint[] }) => Promise<boolean>;
  canApprove?: boolean;
  onApprove?: () => Promise<boolean> | boolean | void;
}) {
  const item = props.deliverable;
  const [reviewing, setReviewing] = useState(false);
  const [walkActive, setWalkActive] = useState(false);
  const walkPointerRef = useRef<((point: Omit<WalkPoint, "t">) => void) | null>(null);

  const [notesVersion, setNotesVersion] = useState(0);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const frameProgressCleanupRef = useRef<(() => void) | null>(null);
  const [salespageProgress, setSalespageProgress] = useState({ percent: 0, atEnd: false });
  const {
    state: frameState,
    attempt: frameAttempt,
    guard,
    retry: retryFrame
  } = useSalespageFrameGuard(`${item?.id ?? "none"}:${item?.url ?? ""}`, Boolean(reviewing && item));
  const bumpNotes = () => setNotesVersion((value) => value + 1);
  const approving = Boolean(item && props.busyAction === `gate-approve:${item.id}`);
  const showApproval = Boolean(item && props.canComment && props.canApprove && item.status === "review");

  useEffect(() => {
    setSalespageProgress({ percent: 0, atEnd: false });
  }, [item?.id, item?.url]);

  const reportSalespageProgress = useCallback(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const state = salespageReaderProgress(frame);
    if (state) setSalespageProgress((previous) => ({
      percent: Math.max(previous.percent, state.percent),
      atEnd: previous.atEnd || state.atEnd
    }));
  }, []);

  const bindSalespageProgress = useCallback((frame: HTMLIFrameElement) => {
    frameProgressCleanupRef.current?.();
    const target = frame.contentWindow;
    try {
      target?.addEventListener("scroll", reportSalespageProgress);
      frame.contentDocument?.addEventListener("scroll", reportSalespageProgress, true);
      frameProgressCleanupRef.current = () => {
        target?.removeEventListener("scroll", reportSalespageProgress);
        frame.contentDocument?.removeEventListener("scroll", reportSalespageProgress, true);
      };
    } catch {
      // Cross-origin artifacts can report the same bounded state by
      // postMessage; the listener below verifies the iframe source.
      frameProgressCleanupRef.current = null;
    }
    reportSalespageProgress();
  }, [reportSalespageProgress]);

  useEffect(() => {
    const onProgressMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow || !isRecord(event.data) || event.data.type !== "launchpad-reader-progress") return;
      const percent = Number(event.data.percent);
      if (!Number.isFinite(percent)) return;
      setSalespageProgress((previous) => ({
        percent: Math.max(previous.percent, Math.min(100, Math.max(0, Math.round(percent)))),
        atEnd: previous.atEnd || event.data.atEnd === true
      }));
    };
    window.addEventListener("message", onProgressMessage);
    return () => {
      window.removeEventListener("message", onProgressMessage);
      frameProgressCleanupRef.current?.();
    };
  }, []);

  const reviewAside = item && props.canComment && props.csrfToken && props.onFeedback ? (
    <div className="gate-doc-actions">
      {showApproval && !salespageProgress.atEnd && (
        <ReaderApprovalAction
          placement="panel"
          unlocked={false}
          progress={salespageProgress.percent}
          approving={approving}
          sending={props.busyAction === `gate-feedback:${item.id}`}
          hintKey="sales.pageApprovalHint"
          t={props.t}
          onApprove={() => void props.onApprove?.()}
        />
      )}
      <WalkFeedback
        csrfToken={props.csrfToken}
        t={props.t}
        pointerRef={walkPointerRef}
        onRecordingChange={setWalkActive}
        draft={{ surface: "deliverable", targetId: props.kind }}
        onSubmit={async (payload) => {
          const delivered = await props.onFeedback!(payload);
          if (delivered) bumpNotes();
          return delivered;
        }}
      />
      <FeedbackComposer
        t={props.t}
        draftKey={props.kind}
        sending={props.busyAction === `gate-feedback:${item.id}`}
        onSubmit={async (payload) => {
          const delivered = await props.onFeedback!(payload);
          if (delivered) bumpNotes();
          return delivered;
        }}
      />
      <DeliverableNotes deliverableId={item.id} csrfToken={props.csrfToken} t={props.t} version={notesVersion} />
    </div>
  ) : undefined;

  return (
    <article className="deliverable-card" data-deliverable-kind={props.kind}>
      <div className="deliverable-card-heading">
        <div><p className="card-kicker">{props.t(`sales.kind.${props.kind}`)}</p><h2>{item ? humanDocTitle(item.title, props.t(`sales.kind.${props.kind}`)) : props.t("sales.preparingTitle")}</h2></div>
        {item && (
          <div className="deliverable-card-statuses">
            <span className="salespage-preview-ready-label">{props.t("sales.previewChip")}</span>
            <span className={`status-pill ${item.status === "approved" ? "pill-done" : "pill-client"}`}>{props.t(`sales.status.${item.status}`)}</span>
          </div>
        )}
      </div>
      {/* F104: honest state on the page card (same law as GateDocCard). */}
      {item?.status === "review" && props.staffReturnedReason && (
        <p className="message staff-returned-note" role="status">
          {props.t("sales.gate.staffReturned").replace("{reason}", props.staffReturnedReason)}
        </p>
      )}
      {item?.status === "approved" && item.approvedByStaff && (
        <p className="message success-message staff-approved-note" role="status">
          {props.t("sales.gate.staffApprovedNote")}
        </p>
      )}
      {!item ? (
        <div className="deliverable-empty"><span className="loading-pulse" /><p>{props.t(`sales.preparing.${props.kind}`)}</p></div>
      ) : (
        <SalespagePreview
          item={item}
          t={props.t}
          onReview={() => setReviewing(true)}
        />
      )}
      {item && (
        <div className="doc-tile-actions salespage-tile-actions">
          <button className="primary-button" type="button" onClick={() => setReviewing(true)}>{props.t("sales.doc.review")}</button>
          <a className="quiet-link salespage-open-link" href={item.url} target="_blank" rel="noreferrer">{props.t("sales.openPreview")}</a>
          <small className="version-pill">{props.t("sales.version").replace("{version}", String(item.version))}</small>
        </div>
      )}
      {reviewing && item && (
        <DocOverlay
          title={item.title}
          kicker={props.t(`sales.kind.${props.kind}`)}
          closeLabel={props.t("sales.doc.close")}
          t={props.t}
          onClose={() => setReviewing(false)}
          immersive
          panelLabel={props.t("sales.doc.panelLabel")}
          walkActive={walkActive}
          aside={reviewAside}
          intro={showApproval
            ? <ReviewLoopIntro material="salespage" placement="reader" t={props.t} />
            : undefined}
        >
          <div className="doc-review-frame-wrap immersive">
            <iframe
              key={`${item.id}:${frameAttempt}`}
              ref={frameRef}
              className="doc-review-frame"
              src={item.url}
              title={item.title}
              sandbox="allow-same-origin allow-scripts"
              onLoad={(event) => {
                guard.loaded();
                bindSalespageProgress(event.currentTarget);
              }}
              onError={guard.failed}
            />
            {frameState === "loading" && (
              <div className="salespage-frame-loading" role="status">
                <strong>{props.t("sales.previewLoading")}</strong>
              </div>
            )}
            {frameState === "released" && (
              <div className="salespage-frame-released" role="alert">
                <span>{props.t("sales.previewError")}</span>
                <button className="secondary-button compact-button" type="button" onClick={retryFrame}>
                  {props.t("sales.previewRetry")}
                </button>
              </div>
            )}
            {walkActive && <WalkCaptureLayer frameRef={frameRef} onPoint={(point) => walkPointerRef.current?.(point)} />}
            {showApproval && salespageProgress.atEnd && (
              <div className="salespage-reader-approval">
                <ReaderApprovalAction
                  placement="end"
                  unlocked
                  progress={salespageProgress.percent}
                  approving={approving}
                  sending={props.busyAction === `gate-feedback:${item.id}`}
                  hintKey="sales.pageApprovalHint"
                  t={props.t}
                  onApprove={() => void props.onApprove?.()}
                />
              </div>
            )}
          </div>
        </DocOverlay>
      )}
    </article>
  );
}

export function salespageReaderProgress(frame: HTMLIFrameElement): { percent: number; atEnd: boolean } | null {
  try {
    const root = frame.contentDocument?.scrollingElement ?? frame.contentDocument?.documentElement;
    if (!root) return null;
    return readerProgressState(root.scrollTop, root.scrollHeight, root.clientHeight);
  } catch {
    return null;
  }
}

export type SalespageFrameState = "loading" | "ready" | "released";

export function createSalespageFrameGuard(
  onState: (state: SalespageFrameState) => void,
  timeoutMs = 12_000
): { loaded(): void; failed(): void; dispose(): void } {
  let settled = false;
  onState("loading");
  const release = (state: Exclude<SalespageFrameState, "loading">) => {
    if (settled) return;
    settled = true;
    globalThis.clearTimeout(timer);
    onState(state);
  };
  const timer = globalThis.setTimeout(() => release("released"), timeoutMs);
  return {
    loaded: () => release("ready"),
    failed: () => release("released"),
    dispose: () => {
      settled = true;
      globalThis.clearTimeout(timer);
    }
  };
}

function useSalespageFrameGuard(identity: string, active = true): {
  state: SalespageFrameState;
  attempt: number;
  guard: { loaded(): void; failed(): void };
  retry(): void;
} {
  const [state, setState] = useState<SalespageFrameState>("loading");
  const [attempt, setAttempt] = useState(0);
  const controllerRef = useRef<ReturnType<typeof createSalespageFrameGuard> | null>(null);
  useEffect(() => {
    controllerRef.current?.dispose();
    if (!active) {
      controllerRef.current = null;
      setState("loading");
      return;
    }
    const controller = createSalespageFrameGuard(setState);
    controllerRef.current = controller;
    return () => {
      controller.dispose();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [active, attempt, identity]);
  const guard = useMemo(() => ({
    loaded: () => controllerRef.current?.loaded(),
    failed: () => controllerRef.current?.failed()
  }), []);
  return {
    state,
    attempt,
    guard,
    retry: () => setAttempt((value) => value + 1)
  };
}

function SalespagePreview(props: {
  item: DeliverableDto;
  t: (key: string) => string;
  onReview: () => void;
}) {
  const [state, setState] = useState<SalespageFrameState>("loading");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setState("loading");
    setAttempt(0);
  }, [props.item.id, props.item.version]);
  const baseSrc = `/api/deliverables/${encodeURIComponent(props.item.id)}/screenshot`;
  const src = attempt > 0 ? `${baseSrc}?retry=${attempt}` : baseSrc;

  return (
    <div className={`preview-frame-wrap preview-state-${state}`} data-salespage-preview-state={state}>
      <button className="preview-shot-button" type="button" onClick={props.onReview} aria-label={props.t("sales.doc.review")}>
        <img
          key={`${props.item.id}-${attempt}`}
          className="preview-shot"
          src={src}
          alt={props.t("sales.previewAlt")}
          loading="lazy"
          onLoad={() => setState("ready")}
          onError={() => setState("released")}
        />
      </button>
      {state !== "ready" && (
        <div
          className={state === "loading" ? "salespage-preview-status salespage-frame-loading" : "salespage-preview-status salespage-frame-released"}
          role={state === "released" ? "alert" : "status"}
        >
          {state === "loading" ? (
            <>
              <span className="salespage-preview-mark" aria-hidden="true" />
              <span className="salespage-preview-lines" aria-hidden="true"><i /><i /><i /></span>
              <strong>{props.t("sales.previewLoading")}</strong>
            </>
          ) : (
            <>
              <strong>{props.t("sales.previewError")}</strong>
              <button className="secondary-button compact-button" type="button" onClick={() => {
                setState("loading");
                setAttempt((value) => value + 1);
              }}>
                {props.t("sales.previewRetry")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
